import { uuidModule } from "@/modules/uuid";
import type {
  AssistantRoutineDeletedSyncUpdateValue,
  AssistantRoutineModelData,
} from "@/store/assistant/types";
import { BaseSyncOperation } from "@/store/sync/operations/BaseSyncOperation";
import { SyncErrorHandlingType } from "@/store/sync/operations/errors/SyncError";
import type { IDeleteAssistantRoutineOperation } from "@/store/sync/operations/types";
import type {
  OptimisticSyncUpdate,
  SyncCustomErrorData,
  SyncModelData,
} from "@/store/sync/types";

export class DeleteAssistantRoutineOperation extends BaseSyncOperation<IDeleteAssistantRoutineOperation> {
  get operationKind(): "DELETE_ASSISTANT_ROUTINE" {
    return "DELETE_ASSISTANT_ROUTINE";
  }

  override get waitForPullConfirmationBeforeOptimisticCleanup(): boolean {
    return true;
  }

  public async generateOptimisticUpdates(): Promise<OptimisticSyncUpdate<SyncModelData>[]> {
    const routine = await this.store.assistantRoutines.getAsync(this.payload.id);
    if (!routine) return [];

    const value: AssistantRoutineDeletedSyncUpdateValue = {
      model_id: this.payload.id,
      model_kind: "ASSISTANT_ROUTINE",
      model_version: routine.modelVersion,
    };

    const optimisticUpdate: OptimisticSyncUpdate<AssistantRoutineModelData> = {
      optimistic_update_id: uuidModule.generate(),
      locally_committed_at: this.committedAt,
      kind: "DELETED",
      value,
    };

    return [optimisticUpdate];
  }

  async triggerRecompute() {
    await this.store.assistantRoutines.recompute(this.payload.id);
  }

  handleInvalidError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handlePermissionDeniedError(_errorData: SyncCustomErrorData) {
    this.ignoreError();
  }

  handleUnknownError(_errorData: SyncCustomErrorData) {
    this.ignoreError(SyncErrorHandlingType.RetryWithLimit);
  }
}
